import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TextField, Button, MenuItem, Box, Typography } from '@mui/material';
import { UserApi, RoleApi } from '../../../apis'; // Adjust the import path as necessary

// Validation schema using Yup
const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string().required('Password is required').min(8, 'Password should be at least 8 characters long'),
  status: Yup.string().required('Status is required'),
  // Not a fixed list: an admin can create roles, so the valid values are
  // whatever the server returns. It rejects an unknown name anyway.
  roleName: Yup.string().required('Role is required'),
});

function MyForm({ fetchUsers,close }) {
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);

  // The assignable roles, which is every role the admin has created. Admin
  // itself is only in the list when the caller is one.
  useEffect(() => {
    let cancelled = false;
    RoleApi.getAssignableRoles()
      .then((res) => {
        if (cancelled) return;
        setRoles(res?.data?.data?.roles || []);
      })
      .catch((error) => {
        console.error('Error loading roles:', error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      password: '',  // Include password field
      status: '',
      roleName: 'User',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);

        // Make the API call to create a user
        const response = await UserApi.createUser(values);

        // Optionally reset the form or navigate to another page here
        close()
        await fetchUsers();
      } catch (error) {
        console.error('Error creating user:', error);
        // Handle the error, e.g., show a notification or alert
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{
        '& .MuiTextField-root': { mb: 2 },
        margin: 'auto',
      }}
    >
      <Typography variant="h7" component="div" gutterBottom>
        Name
      </Typography>
      <TextField
        fullWidth
        id="name"
        name="name"
        value={formik.values.name}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.name && Boolean(formik.errors.name)}
        helperText={formik.touched.name && formik.errors.name}
      />

      <Typography variant="h7" component="div" gutterBottom>
        Email
      </Typography>
      <TextField
        fullWidth
        id="email"
        name="email"
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.email && Boolean(formik.errors.email)}
        helperText={formik.touched.email && formik.errors.email}
      />

      <Typography variant="h7" component="div" gutterBottom>
        Password
      </Typography>
      <TextField
        fullWidth
        id="password"
        name="password"
        type="password"
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.password && Boolean(formik.errors.password)}
        helperText={formik.touched.password && formik.errors.password}
      />

      <Typography variant="h7" component="div" gutterBottom>
        Status
      </Typography>
      <TextField
        fullWidth
        id="status"
        name="status"
        select
        value={formik.values.status}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.status && Boolean(formik.errors.status)}
        helperText={formik.touched.status && formik.errors.status}
      >
        <MenuItem value="Active">Active</MenuItem>
        <MenuItem value="Inactive">Inactive</MenuItem>
      </TextField>

      <Typography variant="h7" component="div" gutterBottom>
        Role
      </Typography>
      <TextField
        fullWidth
        id="roleName"
        name="roleName"
        select
        value={formik.values.roleName}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.roleName && Boolean(formik.errors.roleName)}
        helperText={
          (formik.touched.roleName && formik.errors.roleName) ||
          (roles.find((role) => role.name === formik.values.roleName)?.description ??
            'What each role can do is set under Roles & Permissions.')
        }
      >
        {roles.length === 0 && <MenuItem value="User">Basic User</MenuItem>}
        {roles.map((role) => (
          <MenuItem key={role.id} value={role.name}>
            {role.name}
          </MenuItem>
        ))}
      </TextField>

      <Button color="primary" variant="contained" fullWidth type="submit" disabled={loading}>
        {loading ? 'Submitting...' : 'Add'}
      </Button>
    </Box>
  );
}

export default MyForm;
